import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ChevronDown, RefreshCw } from "react-feather";
import toast from "react-hot-toast";
import { AxiosError } from "axios";
import { Link } from "react-router-dom";
import {
  programService,
  type ProgramRenewalSettings,
} from "@/services/program.service";
import { getRechargeStatus } from "@/services/recharge.service";
import { Button } from "@/components/ui/button";

interface RenewalCommissionCardProps {
  programId: string;
  baseCommissionValue: number;
  settings: ProgramRenewalSettings;
}

type CapMode = "forever" | "limited";

interface Draft {
  enabled: boolean;
  rate: string;
  capMode: CapMode;
  cap: string;
}

function toDraft(s: ProgramRenewalSettings): Draft {
  return {
    enabled: s.renewalsEnabled,
    rate: s.renewalRate ? String(s.renewalRate) : "",
    capMode: s.renewalPaidCap === null ? "forever" : "limited",
    cap: s.renewalPaidCap === null ? "12" : String(s.renewalPaidCap),
  };
}

/**
 * Renewal commissions for Recharge subscriptions. Shown only to brands with
 * Recharge connected: a retail store has no renewals to pay on. The first
 * order of a subscription always pays the program's normal rate; this card
 * decides what every later charge pays, and for how long.
 */
export function RenewalCommissionCard({
  programId,
  baseCommissionValue,
  settings,
}: RenewalCommissionCardProps) {
  const queryClient = useQueryClient();
  const [isOpen, setIsOpen] = useState(false);
  const [draft, setDraft] = useState<Draft>(() => toDraft(settings));

  const { data: recharge } = useQuery({
    queryKey: ["recharge-status"],
    queryFn: getRechargeStatus,
    staleTime: 60_000,
    retry: false,
  });

  useEffect(() => {
    setDraft(toDraft(settings));
  }, [settings.renewalsEnabled, settings.renewalRate, settings.renewalPaidCap]); // eslint-disable-line react-hooks/exhaustive-deps

  const { mutate: save, isPending } = useMutation({
    mutationFn: () =>
      programService.updateProgramRenewals(programId, {
        renewalsEnabled: draft.enabled,
        renewalRateType: "PERCENTAGE",
        renewalRate: Number(draft.rate) || 0,
        renewalPaidCap: draft.capMode === "forever" ? null : Number(draft.cap),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["program", programId] });
      await queryClient.invalidateQueries({ queryKey: ["program"] });
      toast.success(draft.enabled ? "Renewal commissions saved" : "Renewal commissions turned off");
    },
    onError: (err: unknown) => {
      const message = (err as AxiosError<{ message: string | string[] }>)?.response?.data
        ?.message;
      toast.error(Array.isArray(message) ? message[0] : message || "Failed to save");
    },
  });

  // Not connected (or status unknown): nothing to configure here.
  if (!recharge?.connected) return null;

  const saved = toDraft(settings);
  const isDirty = JSON.stringify(draft) !== JSON.stringify(saved);
  const rate = Number(draft.rate);
  const cap = Number(draft.cap);
  const invalid =
    draft.enabled &&
    (!Number.isFinite(rate) ||
      rate <= 0 ||
      rate > 100 ||
      (draft.capMode === "limited" && (!Number.isInteger(cap) || cap < 1)));

  const summary = !settings.renewalsEnabled
    ? "Affiliates earn on the first order only"
    : settings.renewalPaidCap === null
      ? `${settings.renewalRate}% on every renewal, for as long as the customer stays`
      : `${settings.renewalRate}% on the first ${settings.renewalPaidCap} renewal${
          settings.renewalPaidCap === 1 ? "" : "s"
        }`;

  return (
    <section id="PR-renewals-card" className="rounded-xl border border-border bg-card shadow-sm">
      <button
        type="button"
        onClick={() => setIsOpen((open) => !open)}
        className="flex w-full items-center gap-3 px-5 py-3.5 text-left"
        aria-expanded={isOpen}
      >
        <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-brand-100 text-brand-700">
          <RefreshCw className="w-4 h-4" />
        </span>
        <span className="flex-1 min-w-0">
          <span className="block text-sm font-semibold text-foreground">
            Subscription renewals
          </span>
          <span className="block text-xs text-muted-foreground">{summary}</span>
        </span>
        <ChevronDown
          className={`w-5 h-5 text-muted-foreground transition-transform ${isOpen ? "rotate-180" : ""}`}
        />
      </button>

      {isOpen && (
        <div className="border-t border-border px-5 py-4 space-y-4">
          <p className="text-xs text-muted-foreground max-w-prose">
            A subscriber's first order pays your {baseCommissionValue}% program rate as usual.
            With renewals on, the affiliate who brought the subscriber also earns on each
            recurring charge Recharge takes, until the cap you set or the subscription is
            cancelled. Renewals count toward tier thresholds. Read from your{" "}
            <Link to="/brand/integrations/recharge" className="font-medium text-brand-700 hover:underline">
              Recharge connection
            </Link>
            .
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-x-5 gap-y-4">
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">
                Pay on renewals
              </label>
              <div className="flex items-center justify-between rounded-lg border border-border px-3 py-2">
                <p className="text-xs text-muted-foreground">
                  {draft.enabled ? "On" : "Off"}
                </p>
                <button
                  type="button"
                  role="switch"
                  aria-checked={draft.enabled}
                  aria-label="Pay commission on renewals"
                  onClick={() => setDraft({ ...draft, enabled: !draft.enabled })}
                  className={`${
                    draft.enabled ? "bg-brand-600" : "bg-muted"
                  } relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2`}
                >
                  <span
                    className={`${
                      draft.enabled ? "translate-x-5" : "translate-x-0"
                    } pointer-events-none inline-block h-5 w-5 transform rounded-full bg-card shadow ring-0 transition duration-200 ease-in-out`}
                  />
                </button>
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">
                Renewal rate
              </label>
              <div className="relative">
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="0.5"
                  value={draft.rate}
                  disabled={!draft.enabled}
                  onChange={(e) => setDraft({ ...draft, rate: e.target.value })}
                  placeholder={String(Math.round(baseCommissionValue / 2) || 5)}
                  aria-label="Commission on each renewal"
                  className="w-full rounded-lg border border-border bg-transparent px-3 pr-8 py-2 text-sm tabular-nums focus:border-brand-500 focus:ring-2 focus:ring-brand-100 outline-none disabled:opacity-50"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">
                  %
                </span>
              </div>
              <p className="mt-1 text-[11px] text-muted-foreground">
                Of the subscription's product total, per charge.
              </p>
            </div>

            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">
                For how long
              </label>
              <div className="flex gap-2">
                <select
                  value={draft.capMode}
                  disabled={!draft.enabled}
                  onChange={(e) => setDraft({ ...draft, capMode: e.target.value as CapMode })}
                  aria-label="How many renewals pay commission"
                  className="min-w-0 flex-1 rounded-lg border border-border bg-transparent px-3 py-2 text-sm focus:border-brand-500 focus:ring-2 focus:ring-brand-100 outline-none disabled:opacity-50"
                >
                  <option value="forever">Every renewal</option>
                  <option value="limited">First N renewals</option>
                </select>
                {draft.capMode === "limited" && (
                  <input
                    type="number"
                    min="1"
                    step="1"
                    value={draft.cap}
                    disabled={!draft.enabled}
                    onChange={(e) => setDraft({ ...draft, cap: e.target.value })}
                    aria-label="Number of renewals that pay commission"
                    className="w-20 rounded-lg border border-border bg-transparent px-3 py-2 text-sm tabular-nums focus:border-brand-500 focus:ring-2 focus:ring-brand-100 outline-none disabled:opacity-50"
                  />
                )}
              </div>
            </div>
          </div>

          {isDirty && (
            <div className="flex justify-end gap-3 border-t border-border pt-4">
              <button
                type="button"
                onClick={() => setDraft(saved)}
                className="rounded-lg px-4 py-2 text-sm font-medium text-muted-foreground hover:bg-muted"
              >
                Discard
              </button>
              <Button
                id="PR-save-renewals-button"
                type="button"
                disabled={isPending || invalid}
                onClick={() => save()}
              >
                {isPending && (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                )}
                <span>Save changes</span>
              </Button>
            </div>
          )}
        </div>
      )}
    </section>
  );
}
